import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SEOHead from '../../components/SEOHead';
import { calculateCashflowImpact, formatCurrency } from '../../utils/financeCalc';

export default function AssetRefinanceCalculator() {
  const [monthlyRevenue, setMonthlyRevenue] = useState(180000);
  const [monthlyCosts, setMonthlyCosts] = useState(124000);
  const [currentRepayment, setCurrentRepayment] = useState(9500);
  const [newRepayment, setNewRepayment] = useState(7800);

  const result = useMemo(
    () => calculateCashflowImpact({ monthlyRevenue: Number(monthlyRevenue) || 0, monthlyCosts: Number(monthlyCosts) || 0, currentRepayment: Number(currentRepayment) || 0, newRepayment: Number(newRepayment) || 0 }),
    [monthlyRevenue, monthlyCosts, currentRepayment, newRepayment]
  );

  return (
    <>
      <SEOHead
        title="Commercial Cashflow Impact Calculator | Flash Funding"
        description="Assess how repayment changes impact commercial monthly and annual cashflow position."
        canonical="/asset-refinance-calculator"
      />

      <section className="py-16 sm:py-20" data-testid="cashflow-impact-calculator-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-purple-200 bg-white p-6 sm:p-8" data-testid="cashflow-impact-input-card">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-xs text-purple-700" data-testid="cashflow-impact-icon-pill"><Building2 size={14} /> Calculator</div>
            <h1 className="mb-3 text-3xl font-heading text-[#0F0A1A] sm:text-4xl" data-testid="cashflow-impact-heading">Commercial Cashflow Impact Calculator</h1>
            <p className="mb-3 text-sm text-[#4A4556]" data-testid="cashflow-impact-subheading">Model repayment changes and see how they affect business cash flow.</p>
            <p className="mb-6 text-xs text-[#6C657E]" data-testid="cashflow-impact-route-priority-text">
              Start with /car-loans, /personal-loans, /commercial-car-loans, or /business-finance. Use /apply when ready.
            </p>

            <div className="space-y-4">
              <div>
                <Label className="text-[#2E2742]">Monthly Revenue (AUD)</Label>
                <Input type="number" value={monthlyRevenue} onChange={(e) => setMonthlyRevenue(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="cashflow-revenue-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Monthly Operating Costs (AUD)</Label>
                <Input type="number" value={monthlyCosts} onChange={(e) => setMonthlyCosts(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="cashflow-costs-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Current Finance Repayment (AUD)</Label>
                <Input type="number" value={currentRepayment} onChange={(e) => setCurrentRepayment(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="cashflow-current-repayment-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Proposed Repayment (AUD)</Label>
                <Input type="number" value={newRepayment} onChange={(e) => setNewRepayment(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="cashflow-proposed-repayment-input" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-purple-200 bg-white p-6 sm:p-8" data-testid="cashflow-impact-result-card">
            <h2 className="mb-6 text-xl font-semibold text-[#1B1431]">Cashflow Impact</h2>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Net Cashflow (Current)</span>
                <span className="font-mono text-[#1B1431]" data-testid="cashflow-net-before">{formatCurrency(result.netBefore)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Net Cashflow (Proposed)</span>
                <span className="font-mono text-[#1B1431]" data-testid="cashflow-net-after">{formatCurrency(result.netAfter)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Monthly Impact</span>
                <span className="font-mono text-purple-700" data-testid="cashflow-monthly-impact">{formatCurrency(result.monthlyImpact)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Annual Impact</span>
                <span className="font-mono text-[#1B1431]" data-testid="cashflow-annual-impact">{formatCurrency(result.annualImpact)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Repayment Burden (Current)</span>
                <span className="font-mono text-[#1B1431]" data-testid="cashflow-burden-before">{result.repaymentBurdenBefore.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5C5671]">Repayment Burden (Proposed)</span>
                <span className="font-mono text-[#1B1431]" data-testid="cashflow-burden-after">{result.repaymentBurdenAfter.toFixed(1)}%</span>
              </div>
            </div>

            <div className="mt-8 rounded-xl border border-purple-200 bg-[#f8f5fc] p-4">
              <p className="text-xs text-[#5C5671]" data-testid="cashflow-disclaimer">Outputs are indicative and should be used for planning only.</p>
            </div>

            <div className="mt-8">
              <Link to="/business-finance" data-testid="cashflow-calculator-apply-link">
                <Button className="w-full rounded-full bg-purple-600 text-xs uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid="cashflow-calculator-apply-button">
                  See What You Can Get Approved For <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
